import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Moon,
  Sun,
  Search,
  BarChart3,
  Trash2,
  Settings,
  MessageCircle,
  MoreVertical,
  LogOut,
} from 'lucide-react';
import { ChatList } from './components/ChatList';
import { ChatView } from './components/ChatView';
import { CommandPalette } from './components/CommandPalette';
import { StatsPanel } from './components/StatsPanel';
import { Login } from './components/Login';
import { loadPublicChats } from './utils/publicChatsLoader';
import { SearchIndex } from './search/searchIndex';
import { Chat, Theme, SearchResult, ChatStats } from './types';
import './App.css';

type View = 'chats' | 'stats' | 'settings';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [theme, setTheme] = useState<Theme>('light');
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [currentView, setCurrentView] = useState<View>('chats');
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [myNames, setMyNames] = useState<string[]>(['Prabhu', 'Pratik Bhuite']);
  const [pinnedChatIds, setPinnedChatIds] = useState<string[]>([]);
  const [searchIndex] = useState(() => new SearchIndex());

  // Check for existing session
  useEffect(() => {
    const session = localStorage.getItem('isAuthenticated');
    if (session === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('isAuthenticated');
  };

  // Load chats from public folder on mount (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const loadData = async () => {
      setIsLoading(true);
      
      // Load settings from localStorage
      const savedTheme = localStorage.getItem('theme') as Theme | null;
      const savedMyNames = localStorage.getItem('myNames');
      const savedPinnedIds = localStorage.getItem('pinnedChatIds');
      const savedLastChatId = localStorage.getItem('lastOpenedChatId');
      
      if (savedTheme) setTheme(savedTheme);
      if (savedMyNames) setMyNames(JSON.parse(savedMyNames));
      if (savedPinnedIds) setPinnedChatIds(JSON.parse(savedPinnedIds));

      // Load chats from public/chats/
      const loadedChats = await loadPublicChats();
      setChats(loadedChats);
      searchIndex.indexChats(loadedChats);

      if (savedLastChatId) {
        const chat = loadedChats.find((c) => c.id === savedLastChatId);
        if (chat) setSelectedChat(chat);
      }
      
      setIsLoading(false);
    };

    loadData();
  }, [isAuthenticated, searchIndex]);

  // Update theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(true);
      }
      if (e.key === 'Escape') {
        setIsCommandPaletteOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Removed import handler - chats are loaded from public folder

  const handleSelectChat = (chat: Chat) => {
    setSelectedChat(chat);
    localStorage.setItem('lastOpenedChatId', chat.id);
  };

  const handleGlobalSearch = useCallback(
    (query: string): SearchResult[] => {
      return searchIndex.search({
        query,
        caseSensitive: false,
        useRegex: false,
      });
    },
    [searchIndex]
  );

  const handleSelectSearchResult = (result: SearchResult) => {
    const chat = chats.find((c) => c.id === result.chatId);
    if (chat) {
      setSelectedChat(chat);
      setCurrentView('chats');
    }
  };

  const handleClearData = () => {
    if (confirm('This will reload all chats from the public folder. Continue?')) {
      window.location.reload();
    }
  };

  const togglePin = (chatId: string) => {
    const newPinnedIds = pinnedChatIds.includes(chatId)
      ? pinnedChatIds.filter((id) => id !== chatId)
      : [...pinnedChatIds, chatId];
    
    setPinnedChatIds(newPinnedIds);
    localStorage.setItem('pinnedChatIds', JSON.stringify(newPinnedIds));
  };

  const filteredChats = chatSearchQuery
    ? searchIndex.searchChats(chatSearchQuery)
    : chats;

  const stats: ChatStats = {
    totalChats: chats.length,
    totalMessages: chats.reduce((sum, chat) => sum + chat.messageCount, 0),
    topSenders: (() => {
      const senderCounts: Record<string, number> = {};
      chats.forEach((chat) => {
        chat.messages.forEach((msg) => {
          if (msg.sender) {
            senderCounts[msg.sender] = (senderCounts[msg.sender] || 0) + 1;
          }
        });
      });
      return Object.entries(senderCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
    })(),
    activeYears: (() => {
      const years = new Set<number>();
      chats.forEach((chat) => {
        chat.messages.forEach((msg) => {
          if (msg.unixMs > 0) {
            years.add(new Date(msg.unixMs).getFullYear());
          }
        });
      });
      return Array.from(years).sort();
    })(),
    messagesByYear: {},
    messagesByMonth: {},
  };

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="app">
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
        onSearch={handleGlobalSearch}
        onSelectResult={handleSelectSearchResult}
      />

      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="profile-section">
            <div className="profile-avatar">
              <MessageCircle size={20} />
            </div>
            <span className="app-title">WhatsApp</span>
          </div>
          <div className="header-actions">
            <button
              className="icon-button"
              onClick={() => setIsCommandPaletteOpen(true)}
              title="Search (Ctrl+K)"
            >
              <Search size={20} />
            </button>
            <button
              className="icon-button menu-button"
              title="Menu"
            >
              <MoreVertical size={20} />
              <div className="dropdown-menu">
                <button onClick={() => setCurrentView('stats')}>
                  <BarChart3 size={16} />
                  <span>Statistics</span>
                </button>
                <button onClick={() => setCurrentView('settings')}>
                  <Settings size={16} />
                  <span>Settings</span>
                </button>
                <button onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
                  {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
                  <span>{theme === 'light' ? 'Dark' : 'Light'} Mode</span>
                </button>
                <button onClick={handleLogout} style={{ borderTop: '1px solid var(--border)', marginTop: '4px', paddingTop: '8px' }}>
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            </button>
          </div>
        </div>

        {(currentView === 'chats' || !currentView) && (
          <>
            <div className="chat-search-container">
              <div className="search-wrapper">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search or start new chat"
                  value={chatSearchQuery}
                  onChange={(e) => setChatSearchQuery(e.target.value)}
                  className="chat-search-input"
                />
              </div>
            </div>

            <div className="chat-list-container">
              <ChatList
                chats={filteredChats}
                selectedChatId={selectedChat?.id}
                onSelectChat={handleSelectChat}
                searchQuery={chatSearchQuery}
                pinnedChatIds={pinnedChatIds}
                onTogglePin={togglePin}
              />
            </div>
          </>
        )}
      </aside>

      <main className="main-content">
        <AnimatePresence mode="wait">
          {isLoading && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="view-container empty-state whatsapp-style"
            >
              <div className="whatsapp-logo">
                <MessageCircle size={80} />
              </div>
              <h2>Loading your chats...</h2>
              <p className="subtitle">Reading from public/chats/ folder</p>
            </motion.div>
          )}

          {!isLoading && (currentView === 'chats' || !currentView) && selectedChat && (
            <motion.div
              key={`chat-${selectedChat.id}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="view-container"
            >
              <ChatView chat={selectedChat} />
            </motion.div>
          )}

          {!isLoading && (currentView === 'chats' || !currentView) && !selectedChat && chats.length > 0 && (
            <motion.div
              key="no-chat"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="view-container empty-state whatsapp-style"
            >
              <div className="whatsapp-logo">
                <MessageCircle size={80} />
              </div>
              <h2>WhatsApp Chat Viewer</h2>
              <p className="subtitle">
                Send and receive messages without keeping your phone online.
              </p>
              <p className="hint">
                Select a chat from the sidebar to start viewing
              </p>
            </motion.div>
          )}

          {!isLoading && (currentView === 'chats' || !currentView) && chats.length === 0 && (
            <motion.div
              key="no-chats"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="view-container empty-state whatsapp-style"
            >
              <div className="whatsapp-logo">
                <MessageCircle size={80} />
              </div>
              <h2>No Chats Found</h2>
              <p className="subtitle">
                Add .txt files to public/chats/ folder and refresh
              </p>
            </motion.div>
          )}

          {currentView === 'stats' && (
            <motion.div
              key="stats"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="view-container"
            >
              <StatsPanel stats={stats} />
            </motion.div>
          )}

          {currentView === 'settings' && (
            <motion.div
              key="settings"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="view-container settings-view"
            >
              <div className="settings-panel">
                <h2>Settings</h2>

                <div className="setting-group">
                  <label>My Names (for identifying your messages)</label>
                  <p className="setting-description">
                    Enter your names as they appear in WhatsApp (comma-separated)
                  </p>
                  <input
                    type="text"
                    value={myNames.join(', ')}
                    onChange={(e) => {
                      const names = e.target.value.split(',').map((n) => n.trim());
                      setMyNames(names);
                      localStorage.setItem('myNames', JSON.stringify(names));
                    }}
                    placeholder="e.g., Prabhu, Pratik Bhuite"
                  />
                </div>

                <div className="setting-group">
                  <label>Theme</label>
                  <div className="theme-buttons">
                    <button
                      className={`theme-button ${theme === 'light' ? 'active' : ''}`}
                      onClick={() => setTheme('light')}
                    >
                      <Sun size={18} />
                      <span>Light</span>
                    </button>
                    <button
                      className={`theme-button ${theme === 'dark' ? 'active' : ''}`}
                      onClick={() => setTheme('dark')}
                    >
                      <Moon size={18} />
                      <span>Dark</span>
                    </button>
                  </div>
                </div>

                <div className="setting-group danger-zone">
                  <label>Actions</label>
                  <button className="danger-button" onClick={handleClearData}>
                    <Trash2 size={18} />
                    <span>Reload All Chats</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

export default App;
